import { Navigate } from "react-router-dom";
import React, { useState, useEffect, useCallback } from "react";

import * as yup from "yup";
import { useFormik } from "formik";

import { __login } from "../../api/auth";
import useAuth from "../../hooks/useAuth";
import useAxios from "../../hooks/useAxios";
import { isTextchest, getLogoPath } from "../../partners";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ServerMaintenance from "./ServerMaintenance";

// ── TextChest-specific branded login card ─────────────────────────────────────
const TextchestLoginCard = ({ formik, loading, details }) => {
  const [showPassword, setShowPassword] = useState(false);
  const logo = getLogoPath();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#EFF3FB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        p: 2,
      }}
    >
      {/* Decorative background rings */}
      {[
        { size: 500, top: -180, left: -180 },
        { size: 320, bottom: -100, right: -80 },
        { size: 200, top: "40%", left: -60 },
      ].map((style, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: style.size,
            height: style.size,
            borderRadius: "50%",
            border: "2px solid rgba(24, 119, 242, 0.12)",
            top: style.top ?? "auto",
            bottom: style.bottom ?? "auto",
            left: style.left ?? "auto",
            right: style.right ?? "auto",
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Login card */}
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        noValidate
        autoComplete="off"
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Brand header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 3,
          }}
        >
          {logo && (
            <Box
              component="img"
              src={logo}
              alt="TextChest"
              sx={{ height: 36, width: 36, objectFit: "contain" }}
            />
          )}
          <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
            TextChest
          </Typography>
          <Chip
            label="VOICE"
            size="small"
            color="primary"
            sx={{ fontSize: "0.65rem", fontWeight: 700, height: 20, borderRadius: 1 }}
          />
        </Box>

        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 700, textAlign: "center", mb: 1 }}
        >
          Voice Portal Login
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", mb: 3, lineHeight: 1.6 }}
        >
          Access and manage your voice-enabled lines, voicemails, and call activity.
        </Typography>

        {/* Username */}
        <FormControl
          fullWidth
          error={formik.touched.username && Boolean(formik.errors.username)}
          sx={{ mb: 2 }}
        >
          <InputLabel htmlFor="tc-username">Username</InputLabel>
          <OutlinedInput
            id="tc-username"
            name="username"
            label="Username"
            placeholder="Enter your username"
            value={formik.values.username}
            onChange={formik.handleChange}
            startAdornment={
              <InputAdornment position="start">
                <PersonOutlineIcon color="action" />
              </InputAdornment>
            }
          />
          {formik.touched.username && formik.errors.username && (
            <FormHelperText>{formik.errors.username}</FormHelperText>
          )}
        </FormControl>

        {/* Password */}
        <FormControl
          fullWidth
          error={formik.touched.password && Boolean(formik.errors.password)}
          sx={{ mb: 3 }}
        >
          <InputLabel htmlFor="tc-password">Password</InputLabel>
          <OutlinedInput
            id="tc-password"
            name="password"
            type={showPassword ? "text" : "password"}
            label="Password"
            placeholder="Enter your password"
            value={formik.values.password}
            onChange={formik.handleChange}
            startAdornment={
              <InputAdornment position="start">
                <LockOutlinedIcon color="action" />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            }
          />
          {formik.touched.password && formik.errors.password && (
            <FormHelperText>{formik.errors.password}</FormHelperText>
          )}
        </FormControl>

        {details && (
          <FormHelperText error sx={{ textAlign: "center", mb: 2 }}>
            {details}
          </FormHelperText>
        )}

        <LoadingButton
          type="submit"
          size="large"
          loading={loading}
          variant="contained"
          fullWidth
          sx={{ py: 1.5, fontSize: "1rem", fontWeight: 600 }}
        >
          Log In
        </LoadingButton>
      </Box>
    </Box>
  );
};

// ── Default login (existing) ──────────────────────────────────────────────────
const DefaultLoginCard = ({ formik, loading, details }) => (
  <Grid sx={{ maxWidth: "460px", mx: "auto" }}>
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      padding={2}
    >
      <Box
        component="form"
        sx={{ width: "100%", boxShadow: 3, borderRadius: 2, p: 4 }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <Typography
          variant="h4"
          gutterBottom
          component="div"
          sx={{ textAlign: "center" }}
        >
          LOGIN
        </Typography>
        <Grid>
          <Grid item xs={12} sx={{ mb: 3, mt: 2 }}>
            <FormControl
              error={formik.touched.username && Boolean(formik.errors.username)}
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="username">Username</InputLabel>
              <OutlinedInput
                name="username"
                label="Username"
                id="username"
                value={formik.values.username}
                onChange={formik.handleChange}
              />
              {formik.touched.username && Boolean(formik.errors.username) && (
                <FormHelperText id="username-error-text">
                  {formik.errors.username}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ mb: 3 }}>
            <FormControl
              error={formik.touched.password && Boolean(formik.errors.password)}
              sx={{ width: "100%" }}
            >
              <InputLabel htmlFor="password">Password</InputLabel>
              <OutlinedInput
                type="password"
                name="password"
                label="Password"
                id="password"
                value={formik.values.password}
                onChange={formik.handleChange}
              />
              {formik.touched.password && Boolean(formik.errors.password) && (
                <FormHelperText id="password-error-text">
                  {formik.errors.password}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl
              error={Boolean(details)}
              sx={{ width: "100%", textAlign: "center" }}
            >
              <FormHelperText id="login-error-text">{details}</FormHelperText>
            </FormControl>
          </Grid>
          <LoadingButton
            type="submit"
            size="large"
            loading={loading}
            variant="contained"
            fullWidth
          >
            Login
          </LoadingButton>
        </Grid>
      </Box>
    </Grid>
  </Grid>
);

// ── Main Login component ──────────────────────────────────────────────────────
const Login = () => {
  const isServerUnderMaintenance = process.env.REACT_APP_MAINTENANCE_MODE;
  const [details, setDetails] = useState("");
  const [formData, setFormData] = useState(null);
  const { auth, setUserData, setAccessTokenData, setRefreshTokenData } = useAuth();

  const validationSchema = yup.object({
    username: yup.string().required("Username is required"),
    password: yup.string().required("Password is required"),
  });

  const formik = useFormik({
    initialValues: { username: "", password: "" },
    validationSchema,
    onSubmit: (values) => {
      const data = new FormData();
      Object.keys(values).forEach((key) => data.append(key, values[key]));
      setFormData(data);
    },
  });

  const [response, call, loading] = useAxios({ ...__login, data: formData });

  const authentication = useCallback(() => {
    setAccessTokenData(response.data.access_token);
    setRefreshTokenData(response.data.refresh_token);
    setUserData(response.data.user);
  }, [response]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setTimeout(() => {
        authentication();
      }, 10);
    } else if (response?.data?.detail) {
      setDetails(response.data.detail);
    }
  }, [response]);

  useEffect(() => {
    if (formData) {
      setDetails(null);
      call();
    }
  }, [formData]);

  if (auth.token && !isServerUnderMaintenance) {
    return <Navigate to="/" />;
  }

  if (isServerUnderMaintenance) {
    return <ServerMaintenance />;
  }

  const cardProps = { formik, loading, details };
  return isTextchest() ? (
    <TextchestLoginCard {...cardProps} />
  ) : (
    <DefaultLoginCard {...cardProps} />
  );
};

export default Login;
